import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { RootState } from '../../app/store';
import { AuthorizedRequestArg } from '../signin/auth';
import * as authFetch from '../signin/authFetch';
import { GetEntriesResponse } from '../timeseries/timeseriesSlice';
import { listMeasurements } from './measurementsSlice';
import { Measurement, MeasurementUpdate } from './types';

type Status = 'idle' | 'loading' | 'succeeded' | 'failed';

interface MeasurementState {
  status: Status;
  error: string | null;
  data: GetMeasurementResponse | null;
};

interface GetMeasurementArg extends AuthorizedRequestArg {
  measurementID: string;
  includeEntries: boolean;
};

interface GetMeasurementRequest {
  getMeasurement: GetMeasurementArg;
};

interface GetMeasurementResponse {
  measurement: Measurement;
  entries: GetEntriesResponse;
};

export const getMeasurement = createAsyncThunk('measurement/getMeasurement', async (arg: GetMeasurementArg, { dispatch }) => {
  const response = await authFetch.post<GetMeasurementRequest, GetMeasurementResponse>(
    `${process.env.REACT_APP_SERVER_ROOT}/Goals`,
    {
      'getMeasurement': arg,
    },
    arg.authToken,
    dispatch,
  );
  return response.data || null;
});

interface WriteMeasurementArg extends AuthorizedRequestArg {
  measurement: MeasurementUpdate;
};

interface WriteMeasurementRequest {
  writeMeasurement: WriteMeasurementArg;
};

interface WriteMeasurementResponse {
  measurement: Measurement;
};

export const writeMeasurement = createAsyncThunk('measurement/writeMeasurement', async (arg: WriteMeasurementArg, { dispatch }) => {
  const response = await authFetch.post<WriteMeasurementRequest, WriteMeasurementResponse>(
    `${process.env.REACT_APP_SERVER_ROOT}/Goals`,
    {
      'writeMeasurement': arg,
    },
    arg.authToken,
    dispatch,
  );
  dispatch(listMeasurements({ authToken: arg.authToken }));
  if (arg.measurement.id) {
    dispatch(getMeasurement({
      measurementID: arg.measurement.id,
      includeEntries: false,
      authToken: arg.authToken,
    }));
  }
  return response.message;
});

export const measurementSlice = createSlice({
  name: 'measurement',
  initialState: {
    status: 'idle',
    error: null,
    data: null,
  } as MeasurementState,
  reducers: {
  },
  extraReducers: builder => {
    builder.addCase(getMeasurement.pending, (state, action) => {
      // Clear out state if we're loading a new measurement.
      if (state.data?.measurement?.id !== action.meta.arg.measurementID) {
        state.data = null;
      }
      state.error = null;
      state.status = 'loading';
    });
    builder.addCase(getMeasurement.fulfilled, (state, action) => {
      state.status = 'succeeded';
      console.log('Success', action.payload);
      state.data = action.payload;
    });
    builder.addCase(getMeasurement.rejected, (state, action) => {
      state.data = null;
      state.status = 'failed';
      console.log('Error', action.error.message)
      if (action.payload) {
        state.error = action.payload as string;
      } else {
        state.error = action.error.message || null;
      }
    });

    builder.addCase(writeMeasurement.pending, (state, action) => {
      state.status = 'loading';
      state.error = null;
    });
    builder.addCase(writeMeasurement.fulfilled, (state, action) => {
      state.status = 'succeeded';
      console.log('Success', action.payload);
    });
    builder.addCase(writeMeasurement.rejected, (state, action) => {
      state.status = 'failed';
      console.log('Error', action.error.message)
      if (action.payload) {
        state.error = action.payload as string;
      } else {
        state.error = action.error.message || null;
      }
    });
  }
});

// export const { } = measurementSlice.actions

export const selectMeasurement = (state: RootState) => state.measurement.data?.measurement || null;
export const selectEntries = (state: RootState) => state.measurement.data?.entries || { series: [] };

export default measurementSlice.reducer;